import {
  Box,
  Button,
  Divider,
  Flex,
  IconButton,
  Img,
  Stack,
  Tooltip,
} from '@chakra-ui/react';
import Head from 'next/head';
import { ArrowForwardIcon, EditIcon, ExternalLinkIcon } from '@chakra-ui/icons';
import dynamic from 'next/dynamic';
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-scroll';
import { CustomStarFilled } from '../../../config/icons';
import {
  Hotel as THotel,
  HotelFragmentFragment,
} from '../../../generated/types';
import { pluralize } from '../../../utils/helper';
import Container from '../../Layout/Container';
import FeatureList from './FeatureList';
import PageHeader from '../../Layout/PageHeader';
import PageImages, { SliderImage } from '../../Common/PageImages';
import RatingBar from './RatingBar';
import HotelRatingList from './HotelRatingList';
import SidebarWrapper from '../../Layout/SidebarWrapper';
import BookingForm from './Forms/BookingForm';
import RatingForm from './Forms/RatingForm';
import ActivityTeaser from '../Activity/ActivityTeaser';
import OfferTeaser from './OfferTeaser';
import { trackEvent } from '../../../utils/analytics';
import ContentTitle from '../../Common/ContentTitle';
import ContentBlock from '../../Common/ContentBlock';
import PoiTeaser from '../Poi/PoiTeaser';
import TeaserSlider from '../../Common/TeaserSlider';
import TabTeaserGrid from '../../Common/TabTeaserGrid';
import getIconColorForCategoryKey from '../../../utils/getIconColorForCategoryKey';
import getIconForCategoryKey from '../../../utils/getIconForCategoryKey';
import getIconForFeature from '../../../utils/getIconForFeature';
import { useStore } from '../../../store/provider';
import { FacebookProvider, Page } from 'react-facebook';
import { jsonLdScriptProps } from 'react-schemaorg';
import { Hotel as HotelType, WithContext } from 'schema-dts';
import { useRouter } from 'next/router';
import { entityFilterConditionToQueryString } from '../../../utils/parseFilterQueryString';
import Wrapper from '../Wrapper';
import ContactPopoverButton from './ContactPopoverButton';
import useHotelMapMarkers from '../../../hooks/useHotelMapMarkers';
import useHotelNearData from '../../../hooks/useHotelNearData';
import useHotelGroupedData from '../../../hooks/useHotelGroupedData';
import getHotelJsonLd from '../../../utils/getHotelJsonLd';
import HotelRatingButton from '../../Common/HotelRatingButton';

const HotelMap = dynamic(() => import('../../Common/HotelMap'), {
  ssr: false,
});

interface HotelProps {
  data: HotelFragmentFragment;
}

const Hotel: React.FC<HotelProps> = ({ data }) => {
  const store = useStore();
  const router = useRouter();

  let SHOULD_RENDER_FREIZEITHOTELS_TEASER = false;

  const images: SliderImage[] = [data.teaserImage as SliderImage];
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [showBookingModal, setShowBookingModal] = useState(false);

  if (data.detailsForFrontend && data.detailsForFrontend.images) {
    data.detailsForFrontend.images.forEach((image: any) => {
      images.push(image);
    });
  }

  const totalRatingCount = data.averageRating?.ratingCount || 0;

  const onResetRating = () => {
    // TODO: implement
  };

  useEffect(() => {
    if (showRatingModal) {
      trackEvent('HotelDetail', 'Open', 'Rating Modal');
    }
  }, [showRatingModal]);

  if (data.frontends) {
    data.frontends.forEach((frontend) => {
      if (frontend.name === 'Freizeithotels') {
        // this hotel is also visible for freizeithotels
        if (store.config.renderConfig.freizeithotelsTeaser) {
          SHOULD_RENDER_FREIZEITHOTELS_TEASER = true;
        }
      }
    });
  }

  const mapMarkers = useHotelMapMarkers(data);
  const { hotelNearActivities, hotelNearPois } = useHotelNearData(data);
  const {
    groupedActivities,
    groupedActivitiesFreizeithotels,
  } = useHotelGroupedData(data);

  const jsonLD = useMemo<WithContext<HotelType>>(() => getHotelJsonLd(data), [
    data,
  ]);

  return (
    <Wrapper typename={data.__typename}>
      <Box
        {...{
          'data-track-content': '',
          'data-content-name': 'Hotel Detail',
          'data-content-target': data.title,
        }}
      >
        <Head>
          <script {...jsonLdScriptProps<HotelType>(jsonLD)} />
        </Head>

        <RatingForm
          isOpen={showRatingModal}
          hotelId={data.id}
          onClose={() => setShowRatingModal(false)}
          onSubmitted={onResetRating}
        />
        <BookingForm
          isOpen={showBookingModal}
          hotelId={data.id}
          onClose={() => setShowBookingModal(false)}
        />
        <PageHeader
          breadcrumb={[
            {
              href: '/',
              label: 'Startseite',
            },
            {
              href: '/entdecken/hotel',
              label: 'Hotels',
            },
            {
              href: `/entdecken/hotel?filter=${encodeURIComponent(
                entityFilterConditionToQueryString({
                  field: 'country_tid',
                  operator: '=',
                  value: data.country?.id + '',
                })
              )}`,
              label: data.country?.name,
            },
            {
              href: `/entdecken/hotel?filter=${encodeURIComponent(
                entityFilterConditionToQueryString({
                  field: 'region_tid',
                  operator: '=',
                  value: data.region?.id + '',
                })
              )}`,
              label: data.region?.name,
            },
            {
              href: `${data.url}`,
              label: data.title,
              isCurrentPage: true,
            },
          ]}
          title={data.title}
          TitleAdditionComponent={
            <Link to="bewertungen" smooth>
              <HotelRatingButton
                rating={data.averageRating!.total.toFixed(2)}
                totalRatingCount={totalRatingCount}
              />
            </Link>
          }
          subTitle={`${data.region?.name}, ${data.street}, ${data.postcode} ${data.city}`}
          ActionComponent={
            <Button
              size="sm"
              variant="outline"
              colorScheme="blue"
              onClick={() => setShowRatingModal(true)}
            >
              <EditIcon marginRight={2} />
              Bewertung schreiben
            </Button>
          }
        />

        <Container paddingY={4}>
          <Flex
            flexDirection={{
              base: 'column',
              md: 'column',
              lg: 'row',
            }}
          >
            <Box overflow="hidden" flex="1" p="3" pl="0">
              <PageImages images={images} />
            </Box>
            <SidebarWrapper>
              <Box>
                {data.company && <Box fontWeight="bold">{data.company}</Box>}
                {data.street && <Box>{data.street}</Box>}
                {(data.postcode || data.city) && (
                  <Box>
                    {data.postcode} {data.city}
                  </Box>
                )}
                <Divider marginY={6} />
              </Box>

              <ContactPopoverButton {...data} />
              {data.onlineBookingUrl && (
                <>
                  <Button
                    marginTop={2}
                    width="100%"
                    variant="outline"
                    colorScheme="brand"
                    as="a"
                    target="_blank"
                    href={data.onlineBookingUrl}
                  >
                    Online buchen
                    <ExternalLinkIcon marginLeft={2} />
                  </Button>
                </>
              )}
              <Button
                width="100%"
                marginTop={2}
                variant="outline"
                colorScheme="blue"
                onClick={() => setShowBookingModal(true)}
              >
                Buchungsanfrage senden
              </Button>

              {data.highlightedFeaturesForFrontend && (
                <Box>
                  <Divider marginY={4} />
                  <Flex alignItems="center">
                    {data.highlightedFeaturesForFrontend.map((feature) => {
                      if (!feature.icon) return null;

                      const Icon = getIconForFeature(feature.icon);

                      if (!Icon) return null;

                      return (
                        <Box key={feature.id}>
                          <Tooltip label={feature.name} hasArrow>
                            <Flex
                              alignItems="center"
                              justifyContent="center"
                              borderRadius="md"
                              w="45px"
                              h="45px"
                              bg="brand.600"
                              color="#fff"
                              fontSize="3xl"
                              marginRight={1}
                            >
                              <Icon />
                            </Flex>
                          </Tooltip>
                        </Box>
                      );
                    })}
                  </Flex>

                  <Button
                    to="ausstattung"
                    as={Link}
                    smooth
                    w="100%"
                    cursor="pointer"
                    colorScheme="brand"
                    variant="outline"
                    marginTop={2}
                  >
                    Weitere Ausstattung
                    <ArrowForwardIcon marginLeft={2} />
                  </Button>
                </Box>
              )}
            </SidebarWrapper>
          </Flex>
        </Container>

        {SHOULD_RENDER_FREIZEITHOTELS_TEASER && (
          <Box bg="#be9c52" p={8}>
            <Container>
              <Flex justifyContent="space-between" alignItems="center">
                <Box>
                  <Flex
                    alignItems="center"
                    borderRadius="lg"
                    flexDir="column"
                    p={6}
                    bg="#fff"
                    shadow="md"
                  >
                    <Img maxHeight="60px" src="/freizeithotels/logo.svg" />
                    <Flex
                      fontWeight="bold"
                      color="#be9c52"
                      fontSize="2xl"
                      marginTop={2}
                      flex={1}
                      justifyContent="center"
                    >
                      Premium Partner
                    </Flex>
                  </Flex>
                </Box>
                <Flex alignItems="center" flexDir="column">
                  <Box textAlign="center" fontSize="xl">
                    Hier geht es zum gesamten Freizeit- & Aktivangebot des
                    Hotels
                  </Box>
                  <Flex
                    bg="gray.200"
                    borderRadius="lg"
                    marginTop={4}
                    alignItems="center"
                    p={2}
                  >
                    <Stack isInline>
                      {groupedActivitiesFreizeithotels.map((group) => {
                        const categoryColor = getIconColorForCategoryKey(
                          group.category.key!
                        );
                        const Icon = getIconForCategoryKey(group.category.key!);
                        const isHotel = group.category.key === 'hotel';

                        if (!Icon) return null;

                        return (
                          <Tooltip
                            key={group.category.key!}
                            hasArrow
                            label={group.category.name}
                            zIndex={999}
                            placement="top"
                          >
                            <Box>
                              <IconButton
                                as="a"
                                target="_blank"
                                href={`https://www.freizeithotels.info${data.url}#reizeit-und-aktivangebot`}
                                bg={isHotel ? categoryColor : '#fff'}
                                _hover={{
                                  bg: categoryColor,
                                  color: '#fff',
                                }}
                                aria-label={group.category.name}
                                icon={<Icon />}
                              />
                            </Box>
                          </Tooltip>
                        );
                      })}
                    </Stack>
                  </Flex>
                </Flex>
              </Flex>
            </Container>
          </Box>
        )}

        <Container paddingBottom={16}>
          {data.activities && data.activities.length > 0 && (
            <Box id="freizeit-und-aktivangebot">
              <ContentBlock>
                <TabTeaserGrid
                  subTitle={
                    store.config.titles?.hotel?.activities ||
                    'Freizeit- & Aktivangebot'
                  }
                  tabs={groupedActivities || []}
                />
              </ContentBlock>
            </Box>
          )}

          {data.pois && data.pois.length > 0 && (
            <ContentBlock>
              <TeaserSlider
                TitleComponent={
                  <ContentTitle title="Ausflugsziele & Sehenswertes" />
                }
                CardComponent={PoiTeaser}
                data={data.pois || []}
              />
            </ContentBlock>
          )}

          {data.detailsForFrontend && (
            <ContentBlock title="Beschreibung">
              <Flex>
                <Box flex={1}>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: data.detailsForFrontend.body!,
                    }}
                  />
                </Box>
                {data.facebookPageUrl && (
                  <Box marginLeft={8} w="300px">
                    <FacebookProvider appId="302504163426957">
                      <Page tabs="timeline" href={data.facebookPageUrl} />
                    </FacebookProvider>
                  </Box>
                )}
              </Flex>
            </ContentBlock>
          )}

          {data.offersForFrontend && data.offersForFrontend.length > 0 && (
            <ContentBlock>
              <TeaserSlider
                TitleComponent={<ContentTitle title="Angebote" />}
                data={data.offersForFrontend || []}
                CardComponent={OfferTeaser}
              />

              <Box
                marginTop={8}
                fontSize="sm"
                textAlign="center"
                color="gray.500"
              >
                Wenn Du Dich für ein Angebot interessierst, teile das in der
                Nachricht der Buchungsanfrage mit.
              </Box>
            </ContentBlock>
          )}

          <Box marginTop={8} id="ausstattung">
            {data.featuresForFrontend && data.featuresForFrontend.length > 0 && (
              <ContentBlock title="Ausstattung">
                <FeatureList features={data.featuresForFrontend} />
              </ContentBlock>
            )}
          </Box>
        </Container>

        {data.location && (
          <HotelMap
            scrollWheelZoomDelayed
            scrollWheelZoom={false}
            data={data}
            markers={mapMarkers}
            center={[data.location!.lat, data.location!.lng]}
          />
        )}

        <Container paddingY={16} id="bewertungen">
          <Flex alignItems="center" justifyContent="space-between">
            <ContentTitle title="Bewertungen" />
            <Flex alignItems="center">
              {data.ratings!.total > 0 && (
                <CustomStarFilled fill="yellow.500" />
              )}
              <Box ml="2">
                {data.ratings!.total > 0 &&
                  `${data.averageRating!.total.toFixed(
                    2
                  )} (${totalRatingCount} ${pluralize(
                    totalRatingCount,
                    'Bewertung',
                    'Bewertungen'
                  )})`}
              </Box>
            </Flex>
          </Flex>
          {data.ratings!.total > 0 && (
            <Stack marginTop={4}>
              <RatingBar
                title="Verpflegung"
                value={data.averageRating!.catering}
              />
              <RatingBar
                title="Unterkunft"
                value={data.averageRating!.housing}
              />
              <RatingBar
                title="Freundlichkeit"
                value={data.averageRating!.kindness}
              />
            </Stack>
          )}
          <Flex justify="center" marginTop={4}>
            <Button
              size="sm"
              colorScheme="brand"
              onClick={() => setShowRatingModal(true)}
            >
              <EditIcon marginRight={2} />
              Bewertung schreiben
            </Button>
          </Flex>
          <Divider marginY="4" />
          <HotelRatingList hotel={data} data={data.ratings!} />

          {store.config.renderConfig.hotel.nearActivities &&
            hotelNearActivities!.length > 0 && (
              <ContentBlock>
                <TeaserSlider
                  TitleComponent={
                    <ContentTitle title="Aktivitäten in der Nähe" />
                  }
                  CardComponent={ActivityTeaser}
                  data={hotelNearActivities || []}
                />
              </ContentBlock>
            )}

          {store.config.renderConfig.hotel.nearPois &&
            hotelNearPois?.length! > 0 && (
              <ContentBlock>
                <TeaserSlider
                  TitleComponent={
                    <ContentTitle title="Weitere Tipps in der Nähe" />
                  }
                  CardComponent={PoiTeaser}
                  data={hotelNearPois || []}
                />
              </ContentBlock>
            )}
        </Container>
      </Box>
    </Wrapper>
  );
};

export default Hotel;
